'use client';

import React, { useCallback, useMemo, useState } from 'react';
import { kebabCase } from 'es-toolkit/compat';
import { useIntl } from 'react-intl';
import clsx from 'clsx';

import SectionTitle from '../theme/SectionTitle';

import styles from './index.module.scss';
import { Amenity, Settings } from '@/models';

import { AmenityIcon } from './icons';
import { Button } from '../ui/components/core/button';
import { Dialog } from '../ui/components/core/dialog';
import { getAmenitiesLabel } from './getAmenitiesLabel';

type AmenitiesProps = {
  amenities: Settings['property']['amenities'];
  amenitiesV2?: Settings['property']['amenitiesV2'];
  mobileOnly?: boolean;
};

type AmenityItem = {
  key: string;
  icon: string;
  label: string;
};

const mapStructuredAmenities = (
  amenitiesV2: NonNullable<AmenitiesProps['amenitiesV2']>,
): AmenityItem[] => {
  const standard: AmenityItem[] = [...amenitiesV2.amenities]
    .sort((a, b) => {
      if (a.isTop === b.isTop) return 0;
      return a.isTop ? -1 : 1;
    })
    .map((a: Amenity) => ({
      key: a.code,
      icon: a.code,
      label: a.name,
    }));
  const custom: AmenityItem[] = amenitiesV2.customAmenities.map(
    (name: string) => ({
      key: `custom-${name}`,
      icon: 'generic',
      label: name,
    }),
  );
  return [...standard, ...custom];
};

const mapLegacyAmenities = (
  amenities: string[],
  intl: ReturnType<typeof useIntl>,
): AmenityItem[] =>
  amenities.map((amenity) => ({
    key: amenity,
    icon: kebabCase(amenity),
    label: getAmenitiesLabel(intl, amenity),
  }));

export const Amenities = ({
  amenities,
  amenitiesV2,
  mobileOnly,
}: AmenitiesProps) => {
  const intl = useIntl();

  const allAmenities = useMemo(() => {
    if (
      amenitiesV2?.amenities?.length ||
      amenitiesV2?.customAmenities?.length
    ) {
      return mapStructuredAmenities(amenitiesV2);
    }
    return mapLegacyAmenities(amenities || [], intl);
  }, [amenities, amenitiesV2, intl]);

  const visibleAmenities = useMemo(
    () => allAmenities.slice(0, 6),
    [allAmenities],
  );
  const [open, setOpen] = useState(false);

  const openDialog = useCallback(() => setOpen(true), []);

  if (!allAmenities.length) {
    return null;
  }

  return (
    <div
      className={clsx([styles.container, { [styles.mobileOnly]: mobileOnly }])}
    >
      <div className={styles.header}>
        <SectionTitle>
          {intl.formatMessage({
            id: 'amenities',
            defaultMessage: 'Amenities',
          })}
        </SectionTitle>
        {allAmenities.length > visibleAmenities.length && (
          <Dialog
            title={intl.formatMessage({
              id: 'amenities',
              defaultMessage: 'Amenities',
            })}
            placement="center"
            open={open}
            onOpenChange={(e) => setOpen(e.open)}
            trigger={
              <Button variant="plain" onClick={openDialog}>
                {intl.formatMessage({
                  id: 'view_all',
                  defaultMessage: 'View all',
                })}
              </Button>
            }
          >
            <div className={styles.amenities}>
              {allAmenities.map((item) => (
                <div key={item.key} className={styles.amenity}>
                  <AmenityIcon name={item.icon} />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </Dialog>
        )}
      </div>

      <div className={styles.amenities}>
        {visibleAmenities.map((item) => (
          <div key={item.key} className={styles.amenity}>
            <AmenityIcon name={item.icon} />
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
